<script setup lang="ts">
  import { computed, inject } from 'vue';
  import { Tooltip } from '@/shared/ui';
  import { useTableCellStyles } from '../../composables/useTableCellStyles';
  import type { CompiledCell, CompiledTableNode } from '@record-fill';
  import type { TableProperties } from '../../model/form.types';
  import { chunkEdgeOverlayStyles, fillCellContentClass, type ChunkEdge } from './fill-table-styles';
  import { CHANGE_CELL_FILL, type ChangeHighlight } from './change-marks';
  import { FILL_CONTEXT_KEY, SELECTED_NEW_ROW_KEY } from './fill-context';

  const props = defineProps<{
    table: CompiledTableNode;
    cell: CompiledCell;
    change?: ChangeHighlight | null;
    extraRowIndex?: number;
    opensRow?: boolean;
  }>();

  const selectedNewRow = inject(SELECTED_NEW_ROW_KEY, null);
  const rowMarker = computed(
    () => props.opensRow === true && props.extraRowIndex !== undefined && selectedNewRow?.value === props.extraRowIndex,
  );

  const tableProperties = computed(() => props.table.element.properties as TableProperties);

  // The very same style resolver the builder canvas uses, so table border colour, pattern,
  // interior line colour and every custom per-cell border come out identical. Every input is a
  // getter: a cell outlives "+" and the switch between responses in the Logs, and the old last row
  // must lose its outer bottom border.
  const { cellStyles, borderOverlayStyles, outerBorder, borderWidths } = useTableCellStyles({
    get cell() {
      return props.cell.source;
    },
    interactionMode: 'view',
    get totalRows() {
      return props.table.rows;
    },
    get totalColumns() {
      return props.table.columns;
    },
    get borderWidth() {
      return tableProperties.value.borderWidth;
    },
    get borderColor() {
      return tableProperties.value.borderColor;
    },
    get borderStyle() {
      return tableProperties.value.borderStyle;
    },
    get borderPattern() {
      return tableProperties.value.borderPattern;
    },
  });

  // The configured cell fill is an inline style, so the change fill has to override it inline too.
  const styles = computed(() =>
    props.change ? { ...cellStyles.value, backgroundColor: CHANGE_CELL_FILL[props.change.tone] } : cellStyles.value,
  );
  const context = inject(FILL_CONTEXT_KEY, null);
  // Cell ids repeat across tables (a duplicated table keeps them), so the edge is looked up by both.
  const chunkEdges = computed<ChunkEdge[]>(() => {
    const edges = context?.chunkEdges?.value;
    if (!edges) return [];
    return (['top', 'bottom'] as const).filter((edge) => edges[edge][props.table.id]?.includes(props.cell.id));
  });
  const contentClass = computed(() => [
    fillCellContentClass(props.cell),
    { '[&_input]:bg-transparent [&_textarea]:bg-transparent [&_button]:bg-transparent': Boolean(props.change) },
  ]);
</script>

<template>
  <div
    class="relative flex items-center justify-center bg-white-01"
    :style="styles"
    role="gridcell"
    :data-sheet-table="table.id"
    :data-sheet-cell="cell.id"
    :data-sheet-field="cell.node?.kind === 'field' ? '' : undefined"
  >
    <div
      v-if="borderOverlayStyles"
      class="pointer-events-none absolute z-[2]"
      :style="borderOverlayStyles"
    />
    <div
      v-if="rowMarker"
      class="pointer-events-none absolute inset-y-0 left-0 z-[3] w-0.5 bg-[var(--theme-secondary-ink)] opacity-60"
      data-testid="fill-cell-row-marker"
    />
    <div
      v-for="edge in chunkEdges"
      :key="edge"
      class="pointer-events-none absolute z-[2]"
      :style="chunkEdgeOverlayStyles(edge, outerBorder, borderWidths)"
      :data-testid="`fill-cell-chunk-${edge}`"
    />
    <!-- Mounted with or without an old value to show: a mark that appears while the person types
         must not swap the node around the input, or the input remounts and loses focus. -->
    <Tooltip
      v-if="cell.node"
      :content="change?.tooltip ?? ''"
      :focusable="Boolean(change?.tooltip)"
      class="block h-full w-full"
    >
      <div :class="contentClass">
        <slot />
      </div>
    </Tooltip>
  </div>
</template>
